import { InvitationStatus } from './InvitationStatus'
import { useState } from 'react'
import { Card } from '@/components/ui/card'
import { KanbanCard } from '@/components/kanban'
import type { Submission } from '../hooks/useCurationSubmissions'

interface Props {
  submission: Submission
  rankIndex?: number // 1-based; shown on Accepted cards
  onNotesChange: (next: string) => void
  onOpenDetail: () => void
}

export function SubmissionCard({ submission, rankIndex, onNotesChange, onOpenDetail }: Props) {
  const fullName =
    `${submission.applicantInfo.firstName} ${submission.applicantInfo.lastName}`.trim()
  return (
    <KanbanCard id={submission.id}>
      {({ ref, style, attributes, listeners }) => (
        <div ref={ref} style={style} {...attributes} {...listeners}>
          <Card
            onClick={onOpenDetail}
            className="touch-none cursor-grab overflow-hidden p-3 transition-colors hover:bg-accent/40 active:cursor-grabbing"
          >
            <div className="space-y-1.5">
              {rankIndex !== undefined && (
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary/15 text-[10px] font-semibold text-primary">
                  {rankIndex}
                </span>
              )}
              <p className="truncate text-sm font-semibold">{fullName || 'Unknown applicant'}</p>
              <p className="truncate text-xs text-muted-foreground">
                {submission.applicantInfo.email}
              </p>
              <InvitationStatus submission={submission} />
              <CuratorNotesInline value={submission.curatorNotes ?? ''} onCommit={onNotesChange} />
            </div>
          </Card>
        </div>
      )}
    </KanbanCard>
  )
}

function CuratorNotesInline({
  value,
  onCommit,
}: {
  value: string
  onCommit: (next: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)

  function commit() {
    setEditing(false)
    if (draft !== value) onCommit(draft)
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          setDraft(value)
          setEditing(true)
        }}
        onPointerDown={(e) => e.stopPropagation()}
        className="block w-full rounded border border-dashed border-transparent px-1 py-0.5 text-left text-xs text-muted-foreground hover:border-border"
      >
        {value || <span className="italic">Add notes…</span>}
      </button>
    )
  }

  return (
    <textarea
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault()
          setDraft(value)
          setEditing(false)
        }
      }}
      rows={2}
      className="w-full rounded border bg-background px-1 py-0.5 text-xs"
    />
  )
}
